import { Injectable } from '@angular/core';
import {
  AppliedFilter,
  BaseFilter,
  CheckboxFilter,
  ChipsFilter,
  DateRangeFilter,
  FilterCriteria,
  FilterType,
  InputFilter,
  MultiselectFilter,
  SearchCriteria,
} from '@shared/components/filters/Domain/filter.model';
import { TranslateService } from '@ngx-translate/core';
import { PaymentMethodOption } from '@shared/models/payment-catalog.model';

@Injectable({
  providedIn: 'root',
})
export class OperationFilterService {
  constructor(private translate: TranslateService) {}
  public filters: any = null;

  getFilters(
    status: any[],
    collectors: any[],
    paymentsMethods: PaymentMethodOption[],
    portfolios: any[],
  ): (MultiselectFilter | InputFilter | DateRangeFilter | ChipsFilter | CheckboxFilter)[] {
    return [
      {
        id: 'externalIds',
        label: 'Operaciones external IDs',
        type: FilterType.Chips,
        icon: 'pi pi-tag',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_OP'),
      } as ChipsFilter,
      {
        id: 'customerExternalIds',
        label: 'Clientes external IDs',
        type: FilterType.Chips,
        icon: 'pi pi-users',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_CUSTOMER'),
      } as ChipsFilter,
      {
        id: 'customerName',
        label: 'Nombre de cliente',
        type: FilterType.Input,
        placeholder: 'Entra un nombre de cliente',
        icon: 'pi pi-user',
        tooltip:
          "Introduce palabras completas de 3 o más caracteres, no parciales (por ejemplo, usa 'julieta' en lugar de 'juli'). Puedes buscar varias palabras al mismo tiempo separándolas con espacios, guiones, puntos u otros caracteres no alfanuméricos.",
      } as InputFilter,
      {
        id: 'status',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.STATUS'),
        type: FilterType.Checkbox,
        icon: 'pi pi-bell',
        options: [...status]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((status) => {
            return {
              label: this.translate.instant('DATA.COMMON.STATUS.' + status.id),
              value: status.id,
              description: status.description,
            };
          }),
      } as CheckboxFilter,
      {
        id: 'collectorIds',
        label: this.translate.instant('DATA.MODULES.INSTANCES.FORM.LABELS.COLLECTOR_IDS'),
        type: FilterType.Multiselect,
        icon: 'pi pi-box',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.MULTISELECT', {
          nameFilter: 'Perfiles de cobro',
        }),
        options: [...collectors]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((collector) => {
            return {
              label: collector.name,
              value: collector.id,
            };
          }),
      } as MultiselectFilter,
      {
        id: 'paymentMethods',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.PAYMENT_METHOD'),
        type: FilterType.Multiselect,
        icon: 'pi pi-credit-card',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.MULTISELECT', {
          nameFilter: 'Métodos de pago',
        }),
        options: [...paymentsMethods]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((py) => {
            return {
              label: this.translate.instant('DATA.COMMON.PAYMENT_METHODS.' + py.key),
              value: py.key,
            };
          }),
      } as MultiselectFilter,
      {
        id: 'portfolioIds',
        //label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.PORTFOLIOS'),
        label: 'Carteras',
        type: FilterType.Multiselect,
        icon: 'pi pi-wallet',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.MULTISELECT', {
          nameFilter: 'Carteras',
        }),
        options: [...portfolios]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((py) => {
            return {
              label: py.name,
              value: py.id,
            };
          }),
      } as MultiselectFilter,
      {
        id: 'title',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.TITLE'),
        type: FilterType.Input,
        placeholder: 'Enter a title',
        icon: 'pi pi-comment',
        tooltip:
          "Introduce palabras completas de 3 o más caracteres, no parciales (por ejemplo, usa 'julieta' en lugar de 'juli'). Puedes buscar varias palabras al mismo tiempo separándolas con espacios, guiones, puntos u otros caracteres no alfanuméricos.",
      } as InputFilter,
      {
        id: 'dueDateInterval',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.DUE_DATE'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
      {
        id: 'paymentDateInterval',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.PAYMENT_DATE'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
      {
        id: 'createdAtDateInterval',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.CREATION_DATE'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
      {
        id: 'updatedAtDateInterval',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.MODIFICATION_DATE'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
    ];
  }

  convertToAppliedFilters(
    defaultFilters: Record<string, any>,
    filters: BaseFilter[],
  ): AppliedFilter[] {
    return filters
      .map((filter) => {
        let value;

        // Normal filters, just pick by id
        value = defaultFilters[filter.id];
        if (typeof value === 'undefined') {
          return undefined;
        }

        return {
          filter,
          isApplied: true,
          value,
        };
      })
      .filter((appliedFilter): appliedFilter is AppliedFilter => appliedFilter !== undefined);
  }

  buildSearchCriteria(
    filters: FilterCriteria,
    pagination?: { offset: number; limit: number },
  ): SearchCriteria {
    const criteria: SearchCriteria = {
      filters: {},
      pagination: {
        offset: 0,
        limit: 10,
      },
    };

    if (pagination) {
      criteria.pagination = pagination;
    }

    if (!filters) {
      return criteria;
    }

    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null) continue;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) continue;

      // Handle range objects (e.g., amount ranges)
      if (
        typeof value === 'object' &&
        'min' in value &&
        'max' in value &&
        value.min !== value.max
      ) {
        criteria.filters[key] = value;
      }

      // Handle date ranges (start, finish)
      else if (
        typeof value === 'object' &&
        'start' in value &&
        'finish' in value &&
        value.start &&
        value.finish
      ) {
        criteria.filters[key] = {
          start: value.start,
          finish: value.finish,
        };
      }

      // Handle non-empty strings or numbers
      else if (typeof value === 'string' || typeof value === 'number') {
        criteria.filters[key] = value;
      }

      // Handle non-empty arrays
      else if (Array.isArray(value) && value.length > 0) {
        criteria.filters[key] = value;
      }

      // Handle any other plain object
      else if (typeof value === 'object') {
        criteria.filters[key] = value;
      }
    }

    return criteria;
  }
}
